import { useEffect, useId, useLayoutEffect, useState } from 'react';
import type { RespondentConversationState } from '../../lib/multimodal-contract';
import type { ConversationSection } from '../../lib/conversation-sections';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import './ConversationProgress.css';

/** A real count remains mounted while CSS hands its position to the latest revision. */
function ProgressCount({ value, current, animate }: { value: string; current: boolean; animate: boolean }) {
  const [entered, setEntered] = useState(!animate);
  useEffect(() => {
    if (!animate) return undefined;
    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
    };
  }, [animate]);
  return <span className="conversation-progress-count-layer" data-current={current}
    data-phase={current ? entered ? 'current' : 'entering' : 'exiting'}>{value}</span>;
}

/** Compact header progress stays visible when the conversation panel is closed. */
export function ConversationProgress({ progress, sections, activeFieldId }: {
  progress: RespondentConversationState['progress']; sections?: readonly ConversationSection[]; activeFieldId?: string;
}) {
  const sectionId = useId();
  const section = activeFieldId ? sections?.find(candidate => candidate.fieldIds.includes(activeFieldId)) : undefined;
  const fraction = progress.total > 0 ? progress.resolved / progress.total : 0;
  const value = `${progress.resolved}/${progress.total}`;
  const [counts, setCounts] = useState(() => [{ value, animate: false }]);
  const digits = Math.max(String(progress.total).length, String(progress.resolved).length);

  useLayoutEffect(() => {
    setCounts(previous => previous.some(count => count.value === value) ? previous : [...previous, { value, animate: true }]);
  }, [value]);

  useEffect(() => {
    // Retain interrupted layers until the latest entrance ends; stale timers never remove the current count.
    const timeout = setTimeout(() => setCounts(previous => previous.length === 1 ? previous : previous.filter(count => count.value === value)), 800);
    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <div className="flex items-center shrink-0 text-sm tabular-nums text-muted-foreground">
      <span data-section-title id={sectionId} aria-label={section?.title} aria-hidden={!section} inert={!section}
        className={`hidden sm:block min-w-0 max-w-40 overflow-hidden transition-[margin,opacity] duration-300 motion-reduce:transition-none ${section ? 'mr-2 opacity-100' : 'mr-0 opacity-0'}`}
        title={section?.title}><AnimatedLabel text={section?.title ?? ''} truncate /></span>
      <div role="progressbar" aria-label="Conversation progress" aria-describedby={section ? sectionId : undefined} aria-valuemin={0} aria-valuemax={progress.total}
        aria-valuenow={progress.resolved} aria-valuetext={`${progress.resolved} of ${progress.total} resolved, ${progress.answered} answered, ${progress.skipped} skipped`}
        className="h-1 w-12 min-[380px]:w-16 sm:w-24 overflow-hidden rounded-full bg-border">
        <div className="h-full w-full rounded-full bg-volt origin-left transition-transform duration-300 motion-reduce:transition-none" style={{ transform: `scaleX(${fraction})` }} />
      </div>
      <span aria-hidden="true" data-testid="conversation-progress-count" className="conversation-progress-count ml-2" style={{ width: `${digits * 2 + 1}ch` }}>
        {counts.map(count => <ProgressCount key={count.value} {...count} current={count.value === value} />)}
      </span>
    </div>
  );
}
